import { useToast } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useFavoritePrompts } from "@/hooks/usePromptsFavorite";
import { usePinnedPrompts } from "@/hooks/usePromptsPinned";
import { useTranslation } from "@/hooks/useTranslation";
import { PROMPT_CATEGORY_IDS } from "@/lib/prompt-categories";
import { notifyPromptsChanged } from "@/lib/prompt-events";
import {
  getPromptId,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { PinnedContext, PromptCategoryId, PromptDTO } from "@shaarp/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type UsePromptLibraryOptions = {
  context?: PinnedContext;
};

export function usePromptLibrary(options: UsePromptLibraryOptions = {}) {
  const { context } = options;
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const api = useApi();
  const lang = getSupportedLang(i18n.language);
  const [allPrompts, setAllPrompts] = useState<PromptDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { favoriteIds, toggleFavorite } = useFavoritePrompts({
    fetchCustomPrompts: false,
    listenForChanges: true,
  });

  const {
    pinnedIds,
    togglePinned,
    isLimitReached: isPinnedLimitReached,
  } = usePinnedPrompts({
    context,
    fetchCustomPrompts: false,
    listenForChanges: true,
  });

  // Use ref to avoid dependency on api which changes every render
  const apiRef = useRef(api);
  apiRef.current = api;

  const fetchAllPrompts = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      const { data } = await apiRef.current.get<PromptDTO[]>("/prompts");
      setAllPrompts(data);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch prompts"),
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch prompts on mount only
  useEffect(() => {
    let mounted = true;

    const loadPrompts = async () => {
      setError(null);
      setIsLoading(true);
      try {
        const { data } = await apiRef.current.get<PromptDTO[]>("/prompts");
        if (mounted) {
          setAllPrompts(data);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error ? err : new Error("Failed to fetch prompts"),
          );
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadPrompts();

    return () => {
      mounted = false;
    };
  }, []);

  const handleToggleFavorite = useCallback(
    (prompt: PromptDTO) => {
      toggleFavorite(prompt);
    },
    [toggleFavorite],
  );

  const handleTogglePinned = useCallback(
    async (prompt: PromptDTO) => {
      const promptId = getPromptId(prompt);
      const wasPinned = pinnedIds.has(promptId);
      const isNowPinned = await togglePinned(prompt);

      if (!wasPinned && !isNowPinned && isPinnedLimitReached) {
        toast({
          title: t("prompt.pinned.limit.title"),
          description: t("prompt.pinned.limit.description"),
        });
      }
    },
    [pinnedIds, togglePinned, isPinnedLimitReached, toast, t],
  );

  const handleDeletePrompt = useCallback(
    async (promptId: string) => {
      await apiRef.current.delete(`/prompt/${promptId}`);
      toast({
        variant: "default",
        title: t("prompt.select.delete.success.title"),
        description: t("prompt.select.delete.success.description"),
      });
      await fetchAllPrompts();
      // Notify other components about the deleted prompt
      notifyPromptsChanged();
    },
    [toast, t, fetchAllPrompts],
  );

  // Separate prompts by owner type
  const { userPrompts, teamPrompts, publicPrompts } = useMemo(
    () => ({
      userPrompts: allPrompts.filter((p) => p.ownerType === "user"),
      teamPrompts: allPrompts.filter((p) => p.ownerType === "team"),
      publicPrompts: allPrompts.filter((p) => p.ownerType === "public"),
    }),
    [allPrompts],
  );

  // Get favorite prompts
  const favoritePrompts = useMemo(
    () => allPrompts.filter((p) => favoriteIds.has(getPromptId(p))),
    [allPrompts, favoriteIds],
  );

  // Get pinned prompts
  const pinnedPrompts = useMemo(
    () => allPrompts.filter((p) => pinnedIds.has(getPromptId(p))),
    [allPrompts, pinnedIds],
  );

  // Group public prompts by category
  const publicPromptsByCategory = useMemo(() => {
    const grouped = new Map<PromptCategoryId, PromptDTO[]>();
    for (const prompt of publicPrompts) {
      const categoryId = prompt.categoryId || "other";
      const existing = grouped.get(categoryId) || [];
      existing.push(prompt);
      grouped.set(categoryId, existing);
    }
    return grouped;
  }, [publicPrompts]);

  // Filter prompts based on search query
  const filterPrompts = useCallback(
    (prompts: PromptDTO[]): PromptDTO[] => {
      if (!searchQuery.trim()) return prompts;

      const query = searchQuery.toLowerCase();
      return prompts.filter((prompt) => {
        const title = getPromptTitle(prompt, lang);
        const content = prompt.translations?.[lang]?.content || prompt.content;
        return (
          title.toLowerCase().includes(query) ||
          content.toLowerCase().includes(query)
        );
      });
    },
    [searchQuery, lang],
  );

  const filteredFavoritePrompts = filterPrompts(favoritePrompts);
  const filteredPinnedPrompts = filterPrompts(pinnedPrompts);
  const filteredUserPrompts = filterPrompts(userPrompts);
  const filteredTeamPrompts = filterPrompts(teamPrompts);

  // Filter categories that have matching prompts
  const filteredCategories = useMemo(() => {
    return PROMPT_CATEGORY_IDS.filter((categoryId) => {
      const categoryPrompts = publicPromptsByCategory.get(categoryId) || [];
      const filtered = filterPrompts(categoryPrompts);
      return filtered.length > 0;
    });
  }, [publicPromptsByCategory, filterPrompts]);

  const hasResults =
    filteredFavoritePrompts.length > 0 ||
    filteredPinnedPrompts.length > 0 ||
    filteredUserPrompts.length > 0 ||
    filteredTeamPrompts.length > 0 ||
    filteredCategories.length > 0;

  return {
    allPrompts,
    setAllPrompts,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    filteredFavoritePrompts,
    filteredPinnedPrompts,
    filteredUserPrompts,
    filteredTeamPrompts,
    filteredCategories,
    publicPromptsByCategory,
    hasResults,
    handleToggleFavorite,
    handleTogglePinned,
    handleDeletePrompt,
    fetchAllPrompts,
    filterPrompts,
    favoriteIds,
    pinnedIds,
    PROMPT_CATEGORY_IDS,
  };
}
